import { tt } from "../i18n/tt";
/**
 * 권리사 작품 상세 「성과 요약」 순수 계산 — 2026-09-04 QA 1차 #13 (PR-I).
 *
 * ## 왜 이 파일이 있나 — 숫자 3종이 서로 달랐다
 * 같은 작품인데 화면마다 다른 수가 나왔다(2026-09-04 조사, 운영 DB 확인):
 *
 * | 작품 | 참여 채널(구) | 승인 채널 | 제작 영상(구·youtube_studio) | 인사이트 탭 |
 * |---|---|---|---|---|
 * | 유부녀 킬러 | 32 | **16** | 443 | **449** |
 * | 헬스파머 | 30 | **29** | 540 | **297** |
 * | 자매다방 | 49 | **46** | 739 | **448** |
 *
 * 원인
 *  1. `VideoRightsManager.fetchChannelCounts` 가 `channel_to_video` 를 status 필터 없이 세서
 *     신청·거절 채널까지 '참여 채널'에 들어갔다.
 *  2. `get_work_video_counts_by_codes`(youtube_studio DISTINCT content_id) 는 **승인 채널 게이트도
 *     회사 필터도 없다**. 게다가 원천이 정산 CSV라 YouTube Data API 스냅샷 기반 인사이트와 모수가 다르다.
 *  3. 인사이트 탭 RPC `get_youtube_insight_works` 만
 *     `channel.approved_yn = true AND platform='youtube' AND deleted_at IS NULL` 게이트를 가졌다.
 *
 * ## 이번에 통일한 기준
 *  - **참여 채널 수** = `channel_to_video.status = true` 이고 `rejected_bool` 이 아닌 행 (`isApprovedWorkChannel`).
 *    (목록 `WorkChannelList` 는 신청·거절도 보여주므로 목록 길이 ≠ 이 수. 목록 헤더에 기준을 적어 둔다.)
 *  - **제작 영상 수 · 누적 조회수 · 최근 7일 조회수** = 인사이트 탭과 **같은 RPC**
 *    `get_youtube_insight_works(p_company, p_start, p_end, p_keywords)` 한 번으로 전부 얻는다.
 *    새 RPC 를 만들지 않았다 — 게이트가 한 곳(마이그레이션 20260812000001)에만 있어야 다시 어긋나지 않는다.
 *      · `video_count`  = 기간 무관 DISTINCT content_id (vids CTE 에 날짜 조건이 없다)
 *      · `total_views`  = content_id 별 `snapshot_date <= p_end` 최신 스냅샷 합 → 누적
 *      · `view_delta`   = 기간(p_start~p_end) 증가분 → p_start 를 6일 전으로 주면 "최근 7일"
 *  - **최근 업로드 목록** = `get_youtube_insight_work_videos_recent(p_company, p_code, p_limit)`
 *    (마이그레이션 `20260904290001`, QA #53) — 같은 게이트에 `published_at desc` + LIMIT 를 더한 것.
 *    각 행에 `total_count`(게이트 통과 전체 편수)가 실려 「더 보기」 가 남은 개수를 안다.
 *    미적용(PGRST202)이면 기존 `get_youtube_insight_work_videos(p_company, p_code)` (조회수순 전량,
 *    20260709000002) 로 폴백하고 업로드일 정렬은 클라이언트가 한다(`pickRecentUploads`).
 *
 * 모든 함수는 순수 — IO 는 호출부(VideoRightsManager · WorkPerformanceSummary)가 한다.
 */

/** 성과 카드 「최근 N일 조회수」의 N. 바꾸면 카드 라벨(`recentViewsLabel`)도 같이 바뀐다. */
export const RECENT_WINDOW_DAYS = 7;

/**
 * 검수 '대기'로 세는 상태 — `VideoInspectionManage.isPendingStatus` 와 같아야 한다.
 * (`revision_requested` 는 크리에이터가 고치는 중이라 권리사가 처리할 게 없다 → 대기 아님)
 */
export const PENDING_INSPECTION_STATUSES = ["pending", "resubmit_requested"];

/** ISO/Date → KST 기준 "yyyy-MM-dd" (en-CA 로케일이 그 형식 그대로 출력) */
function kstDate(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/**
 * 인사이트 RPC 에 넘길 "최근 N일" 구간(양끝 포함, KST 날짜).
 * days=7 이면 오늘 포함 7일 → start = 6일 전.
 */
export function recentWindow(now = new Date(), days = RECENT_WINDOW_DAYS) {
  const end = kstDate(now);
  const start = kstDate(new Date(now.getTime() - (days - 1) * 86400000));
  return { start, end };
}

export const recentViewsLabel = (days = RECENT_WINDOW_DAYS) => tt("a.39a214b9", "최근 {{v0}}일 조회수", { v0: days });

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * 참여 채널 판정 — **승인된 채널만**. 거절(rejected_bool)은 status 와 독립적으로 켜지므로
 * (WorkChannelList.getApplicationStatus 가 거절을 최우선으로 보는 것과 같은 이유) 함께 제외한다.
 */
export function isApprovedWorkChannel(row) {
  return row?.status === true && row?.rejected_bool !== true;
}

/**
 * `channel_to_video` 행들 → 작품별 { approved, whitelistPending }.
 *  - approved: 승인 채널 수 (= 성과 카드 「참여 채널」)
 *  - whitelistPending: 승인됐지만 `whitelist_bool` 이 아직 false 인 채널 수
 *    (**채널 단위** 작품의 화이트리스트 대기. 영상 단위 작품은 검수 건으로 센다 — `summarizeInspections`)
 */
export function summarizeChannelLinks(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    const key = row?.video_id;
    if (key === undefined || key === null) return;
    if (!map[key]) map[key] = { approved: 0, whitelistPending: 0 };
    if (!isApprovedWorkChannel(row)) return;
    map[key].approved += 1;
    if (row.whitelist_bool !== true) map[key].whitelistPending += 1;
  });
  return map;
}

/**
 * 영상 단위 화이트리스트 대기 건인가 — 화리를 요청했는데(`whitelist_requested`) 아직 미처리이고, 신청 취소가 아닌 건.
 * 2026-10-21: 신청 취소 건은 화면에서 처리 칸이 잠겨 있어(VideoInspectionManage) 대기로 세면 처리할 수 없는 숫자가 남는다 → 뺀다.
 * 「할 일」(src/lib/rightsTodo.js)과 작품 상세 카드가 같은 판정을 쓴다.
 */
export function isVideoWhitelistPending(row) {
  return row?.whitelist_requested === true && row?.whitelist_processed !== true && row?.status !== "cancelled";
}

/**
 * `video_inspection` 행들 → 작품별 { inspectionPending, whitelistPending }.
 *  - inspectionPending: 권리사가 승인/수정요청을 눌러야 하는 건 (pending · resubmit_requested)
 *  - whitelistPending: 크리에이터가 화리를 요청했는데(`whitelist_requested`) 아직 미처리인 건
 *    (**영상 단위** 작품의 화이트리스트 대기 · 신청 취소 제외 — `isVideoWhitelistPending`)
 */
export function summarizeInspections(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    const key = row?.video_id;
    if (key === undefined || key === null) return;
    if (!map[key]) map[key] = { inspectionPending: 0, whitelistPending: 0 };
    if (PENDING_INSPECTION_STATUSES.includes(row.status)) map[key].inspectionPending += 1;
    if (isVideoWhitelistPending(row)) map[key].whitelistPending += 1;
  });
  return map;
}

/**
 * `get_youtube_insight_works` 결과 → 식별코드별 { videoCount, totalViews, recentViews }.
 * RPC 는 매핑 영상이 하나도 없는 작품은 **행 자체를 돌려주지 않는다** → 그 작품은 map 에 없고,
 * 호출부는 0 이 아니라 '집계 전'으로 표시한다(`workStatsOf`).
 */
export function workStatsFromInsightRows(rows) {
  const map = {};
  (rows || []).forEach((r) => {
    if (!r?.identification_code) return;
    map[r.identification_code] = {
      videoCount: num(r.video_count),
      totalViews: num(r.total_views),
      recentViews: num(r.view_delta),
    };
  });
  return map;
}

/**
 * 식별코드로 성과를 꺼낸다. 없으면 `null` — 카드는 0 대신 '집계 전'을 보여준다.
 * 식별코드가 아예 없는 작품(코드 미부여)도 집계 불가라 null.
 */
export function workStatsOf(statsMap, identificationCode) {
  if (!identificationCode) return null;
  return statsMap?.[identificationCode] || null;
}

/**
 * 화이트리스트 대기 건수 — **처리 단위(whitelist_scope)마다 대상이 다르다**.
 *  - channel: 승인됐는데 `whitelist_bool=false` 인 채널 수 (이 화면의 참여 채널 목록에서 처리)
 *  - video  : 요청됐는데 미처리인 검수 건 수 (검수 관리 화면에서 처리)
 *  - 그 외(처리 안 함): `null` — 대기 개념이 없다 → 카드에 '해당 없음'
 */
export function whitelistPendingCount(scope, { channelPending = 0, inspectionPending = 0 } = {}) {
  if (scope === "channel") return channelPending;
  if (scope === "video") return inspectionPending;
  return null;
}

/**
 * 화리 대기를 어디서 처리하는가 — 카드 클릭 목적지 결정용.
 * channel → 이 화면의 참여 채널 목록, video → 검수 관리 화면, 그 외 → 이동 없음.
 */
export function whitelistTarget(scope) {
  if (scope === "channel") return "channels";
  if (scope === "video") return "inspection";
  return null;
}

/** 최근 업로드 목록의 한 페이지(처음 보여주는 수 = 「더 보기」 한 번에 늘리는 수) */
export const RECENT_UPLOADS_PAGE = 5;

/**
 * 「더 보기」 를 n 번 눌렀을 때 서버에 요청할 limit. 화면에 보이는 수와 같다 — 서버가 그만큼만 정렬·반환한다.
 * (예전에는 전량 2,700건을 받아 클라이언트가 잘랐다 — QA #53)
 */
export function recentUploadsLimit(pageCount, page = RECENT_UPLOADS_PAGE) {
  const n = Number(pageCount);
  const size = Number(page) > 0 ? Number(page) : RECENT_UPLOADS_PAGE;
  return Math.max(1, Number.isFinite(n) && n > 0 ? Math.ceil(n) : 1) * size;
}

/**
 * 게이트 통과 전체 편수 — `_recent` RPC 는 각 행에 `total_count` 를 싣는다. 행이 없으면 0.
 * 폴백 RPC(전량)에는 없으므로 행 수를 쓴다.
 */
export function recentUploadsTotal(rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return 0;
  const n = Number(list[0]?.total_count);
  return Number.isFinite(n) && n >= list.length ? n : list.length;
}

/**
 * PostgREST 「그런 함수 없음」 — 마이그레이션이 아직 적용되지 않은 배포 순서에서만 난다.
 * 이때만 기존 RPC 로 폴백한다. 다른 오류(권한·타임아웃)는 폴백하지 않고 실패로 보여준다.
 */
export function isMissingRpcError(error) {
  if (!error) return false;
  if (error.code === "PGRST202") return true;
  const msg = String(error.message || "");
  return /could not find the function/i.test(msg);
}

/**
 * 최근 업로드 N건 — `get_youtube_insight_work_videos` 는 조회수순으로 오므로 업로드일순으로 다시 세운다.
 * published_at 이 없는 행은 뒤로 밀고(동률은 content_id 로 안정 정렬), 원본 배열은 건드리지 않는다.
 */
export function pickRecentUploads(rows, limit = 5) {
  const sorted = [...(rows || [])].sort((a, b) => {
    const ta = a?.published_at ? new Date(a.published_at).getTime() : NaN;
    const tb = b?.published_at ? new Date(b.published_at).getTime() : NaN;
    const va = Number.isNaN(ta) ? -Infinity : ta;
    const vb = Number.isNaN(tb) ? -Infinity : tb;
    if (va !== vb) return vb - va;
    return String(a?.content_id || "").localeCompare(String(b?.content_id || ""));
  });
  return limit === null || limit === undefined ? sorted : sorted.slice(0, limit);
}

/**
 * 업로드된 영상(content_id) ↔ 검수 건 연결.
 *
 * `video_inspection` 에는 content_id 컬럼이 없고 크리에이터가 낸 `youtube_url`
 * (`https://youtube.com/shorts/<content_id>[?...]`) 만 있다 → URL 안에 content_id 가 들어 있는지로 잇는다.
 * 같은 영상에 여러 회차(round)가 있으면 **가장 마지막 것**(created_at 최신)이 현재 상태다.
 * content_id 는 11자라 다른 URL 에 우연히 포함될 가능성은 사실상 없다.
 */
export function inspectionStatusIndex(inspections) {
  const rows = (inspections || [])
    .filter((r) => r?.youtube_url)
    .slice()
    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
  return {
    /** content_id 로 검수 상태 문자열을 찾는다. 없으면 null(= 검수 기록 없음) */
    statusOf(contentId) {
      if (!contentId) return null;
      let found = null;
      rows.forEach((r) => {
        if (String(r.youtube_url).includes(contentId)) found = r.status || null;
      });
      return found;
    },
  };
}
